import { useState, useRef, useEffect } from 'react';
import { Send, MessageCircle } from 'lucide-react';
import { API_URL } from '../config';

interface Message {
  role: 'bot' | 'user';
  content: string;
  timestamp: Date;
}

interface EmbedWidgetProps {
  flowDefinition?: any;
  flowId?: string;
  apiUrl?: string;
  title?: string;
  primaryColor?: string;
  height?: string;
  theme?: 'light' | 'dark' | 'auto';
  animation?: 'slide' | 'fade' | 'bounce' | 'none';
  customCSS?: string;
}

export default function EmbedWidget({ 
  flowDefinition, 
  flowId,
  apiUrl = API_URL,
  title = 'Chat Assistant',
  primaryColor = '#9333ea',
  height = '600px',
  theme = 'light',
  animation = 'slide',
  customCSS = ''
}: EmbedWidgetProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [waitingForInput, setWaitingForInput] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [quickReplies, setQuickReplies] = useState<string[]>([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const [flowDef, setFlowDef] = useState<any>(flowDefinition);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Carica flow definition se viene passato flowId
  useEffect(() => {
    if (flowId && !flowDefinition) {
      // Prova a caricare da localStorage (per sviluppo) o da API
      const saved = localStorage.getItem(`pigna-flow-${flowId}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setFlowDef(parsed);
        } catch (e) {
          console.error('Errore caricamento flow:', e);
        }
      }
    }
  }, [flowId, flowDefinition]);

  // Ascolta messaggi dal parent window (per ricevere flowDefinition)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // In produzione, verificare event.origin per sicurezza
      if (event.data.type === 'pigna-flow-definition') {
        setFlowDef(event.data.flowDefinition);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Leggi flowDefinition e parametri da URL params (se presente)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const flowData = urlParams.get('flow');
    if (flowData && !flowDef) {
      try {
        const decoded = JSON.parse(decodeURIComponent(flowData));
        setFlowDef(decoded);
      } catch (e) {
        console.error('Errore parsing flow da URL:', e);
      }
    }
    
    // Leggi parametri personalizzazione da URL (hanno priorità sui props)
    const urlTheme = urlParams.get('theme') as 'light' | 'dark' | 'auto' | null;
    const urlCustomCSS = urlParams.get('customCSS');
    
    // Applica theme (da URL o da props)
    const finalTheme = urlTheme || theme;
    if (finalTheme) {
      document.documentElement.setAttribute('data-theme', finalTheme);
    }
    
    // Applica custom CSS (da URL o da props)
    const finalCustomCSS = urlCustomCSS ? decodeURIComponent(urlCustomCSS) : customCSS;
    if (finalCustomCSS) {
      const style = document.createElement('style');
      style.id = 'pigna-custom-css';
      style.textContent = finalCustomCSS;
      // Rimuovi stile precedente se esiste
      const existing = document.getElementById('pigna-custom-css');
      if (existing) existing.remove();
      document.head.appendChild(style);
    }
    
    // Animation viene gestita dal componente parent (EmbedPage) per iframe
    // Qui non serve applicarla direttamente
  }, [flowDef, theme, customCSS]);

  const startConversation = async () => {
    if (!flowDef || !flowDef.nodes || flowDef.nodes.length === 0) {
      setMessages([{
        role: 'bot',
        content: '❌ Flow non configurato correttamente.',
        timestamp: new Date(),
      }]);
      return;
    }

    setMessages([]);
    setIsCompleted(false);
    setWaitingForInput(false);
    setIsLoading(true);
    
    const newSessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);

    try {
      const response = await fetch(`${apiUrl}/flow/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flow_definition: flowDef,
          session_id: null,
          user_input: null,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setSessionId(data.session_id);

      if (data.content) {
        setMessages([{
          role: 'bot',
          content: data.content,
          timestamp: new Date(),
        }]);
      }

      if (data.type === 'input_required') {
        setWaitingForInput(true);
        if (data.quick_replies && Array.isArray(data.quick_replies)) {
          setQuickReplies(data.quick_replies);
        }
      } else if (data.type === 'completed') {
        setIsCompleted(true);
      }
    } catch (error: any) {
      console.error('Errore avvio conversazione:', error);
      setMessages([{
        role: 'bot',
        content: `❌ Errore: ${error.message || 'Impossibile connettersi al server'}`,
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (quickReplyValue?: string) => {
    const messageToSend = quickReplyValue || inputValue.trim();
    if (!messageToSend || !sessionId || isLoading || !flowDef) return;

    const userMessage = messageToSend;
    setInputValue('');
    setIsLoading(true);
    setQuickReplies([]);

    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
    }]);

    setWaitingForInput(false);

    try {
      const response = await fetch(`${apiUrl}/flow/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flow_definition: flowDef,
          session_id: sessionId,
          user_input: userMessage,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.content) {
        setMessages(prev => [...prev, {
          role: 'bot',
          content: data.content,
          timestamp: new Date(),
        }]);
      }

      if (data.type === 'input_required') {
        setWaitingForInput(true);
        if (data.quick_replies && Array.isArray(data.quick_replies)) {
          setQuickReplies(data.quick_replies);
        }
      } else if (data.type === 'completed') {
        setIsCompleted(true);
        setWaitingForInput(false);
      } else if (data.type === 'message' && data.next_node) {
        await processNextNode(data.next_node, data.session_id);
      }
    } catch (error: any) {
      console.error('Errore invio messaggio:', error);
      setMessages(prev => [...prev, {
        role: 'bot',
        content: `❌ Errore: ${error.message || 'Errore di comunicazione'}`,
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const processNextNode = async (_nextNodeId: string, sessionId: string, depth: number = 0): Promise<void> => {
    if (depth > 10) return;

    try {
      const response = await fetch(`${apiUrl}/flow/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flow_definition: flowDef,
          session_id: sessionId,
          user_input: null,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.content) {
        setMessages(prev => [...prev, {
          role: 'bot',
          content: data.content,
          timestamp: new Date(),
        }]);
      }

      if (data.type === 'input_required') {
        setWaitingForInput(true);
        if (data.quick_replies && Array.isArray(data.quick_replies)) {
          setQuickReplies(data.quick_replies);
        }
      } else if (data.type === 'completed') {
        setIsCompleted(true);
        setWaitingForInput(false);
      } else if (data.type === 'message' && data.next_node) {
        await processNextNode(data.next_node, data.session_id || sessionId, depth + 1);
      }
    } catch (error) {
      console.error('Errore processamento nodo:', error);
    }
  };

  const handleQuickReply = async (value: string) => {
    setInputValue(value);
    await sendMessage(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Widget minimizzato - mostra solo il bottone
  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50"
        style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
      >
        <button
          onClick={() => setIsMinimized(false)}
          className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full p-4 shadow-2xl hover:shadow-3xl transition-all transform hover:scale-110"
          style={{ backgroundColor: primaryColor }}
          aria-label="Apri chat"
          data-theme={theme}
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      </div>
    );
  }

  // Widget espanso
  return (
    <div 
      className="fixed bottom-4 right-4 z-50 bg-white rounded-lg shadow-2xl flex flex-col"
      style={{ 
        width: '380px', 
        height: height,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        border: `2px solid ${primaryColor}`
      }}
      data-theme={theme}
      data-animation={animation}
    >
      {/* Header */}
      <div 
        className="text-white p-4 flex items-center justify-between rounded-t-lg"
        style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, #3b82f6 100%)` }}
      >
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          <h3 className="font-bold text-lg">{title}</h3>
        </div>
        <button
          onClick={() => setIsMinimized(true)}
          className="hover:bg-white hover:bg-opacity-20 p-1 rounded transition"
          aria-label="Minimizza"
        >
          <span className="text-xl">−</span>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 mt-8">
            <div className="text-4xl mb-4">💬</div>
            <p className="text-sm mb-2">Benvenuto!</p>
            <button
              onClick={startConversation}
              disabled={isLoading || !flowDef}
              className="px-4 py-2 rounded-lg font-semibold text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                backgroundColor: primaryColor,
                color: 'white'
              }}
            >
              {isLoading ? 'Caricamento...' : 'Inizia conversazione'}
            </button>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                msg.role === 'user'
                  ? 'text-white'
                  : 'bg-white border-2 border-gray-200 text-gray-800'
              }`}
              style={msg.role === 'user' ? { backgroundColor: primaryColor } : {}}
            >
              <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
              <div
                className={`text-xs mt-1 ${
                  msg.role === 'user' ? 'text-white text-opacity-80' : 'text-gray-400'
                }`}
              >
                {msg.timestamp.toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border-2 border-gray-200 rounded-2xl px-4 py-2">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}

        {/* Il nodo End non mostra messaggi - conversazione termina silenziosamente */}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {messages.length > 0 && (
        <div className="p-3 bg-white border-t-2 border-gray-200 rounded-b-lg">
          {waitingForInput && !isCompleted ? (
            <div className="space-y-2">
              {quickReplies.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {quickReplies.map((reply, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickReply(reply)}
                      disabled={isLoading}
                      className="px-3 py-1.5 text-white rounded-full text-xs font-semibold transition shadow-md hover:shadow-lg transform hover:scale-105 disabled:opacity-50"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              )}
              
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={quickReplies.length > 0 ? "Oppure scrivi..." : "Scrivi la tua risposta..."}
                  className="flex-1 px-3 py-2 border-2 rounded-lg focus:outline-none text-sm"
                  style={{ borderColor: primaryColor }}
                  disabled={isLoading}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!inputValue.trim() || isLoading}
                  className="text-white px-3 py-2 rounded-lg transition disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-400 text-xs py-2">
              {isCompleted ? 'Conversazione completata' : 'Clicca "Inizia conversazione" per iniziare'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

